import { Button, Select, SelectItem, cn } from '@heroui/react';
import { Prohibition } from 'iconoir-react';

import type { Dataset } from '~/api/experiments';
import i18n from '~/common/helpers/i18n';

type DatasetSelectSectionProps = {
  datasets: Dataset[];
  selectedDatasetId: string | null;
  isLoading: boolean;
  onDatasetChange: (datasetId: string | null) => void;
  learnMoreHref: string;
};

function NoDatasetsEmptyContent({ learnMoreHref }: { learnMoreHref: string }) {
  return (
    <div
      className='flex flex-col items-center gap-4 px-6 py-8 text-center'
      data-testid='monitor-agent-logs-collect-no-datasets'
    >
      <div className='flex size-12 items-center justify-center rounded-[9.6px] bg-surface-container'>
        <Prohibition className='icon-medium text-on-surface-variant' />
      </div>
      <div className='flex flex-col gap-2.5'>
        <p className='text-title-md font-semibold text-on-surface'>
          {i18n.t('scenes.monitor.agentLogs.collect.noDatasetsTitle')}
        </p>
        <p className='text-body-regular-md text-on-surface-variant'>
          {i18n.t('scenes.monitor.agentLogs.collect.noDatasetsDescription')}
        </p>
      </div>
      <Button
        as='a'
        href={learnMoreHref}
        target='_blank'
        rel='noreferrer'
        variant='light'
        size='sm'
        data-testid='monitor-agent-logs-collect-learn-more'
      >
        {i18n.t('scenes.monitor.agentLogs.collect.learnMore')}
      </Button>
    </div>
  );
}

export function DatasetSelectSection({
  datasets,
  selectedDatasetId,
  isLoading,
  onDatasetChange,
  learnMoreHref
}: DatasetSelectSectionProps) {
  const hasDatasets = datasets.length > 0;

  return (
    <div
      className='flex w-full gap-7'
      data-testid='monitor-agent-logs-collect-dataset-section'
    >
      <div className='flex w-[372px] shrink-0 flex-col gap-1.5'>
        <h3 className='text-title-md font-semibold text-on-surface'>
          {i18n.t('scenes.monitor.agentLogs.collect.createTestCase')}
        </h3>
        <p className='text-body-regular-md text-on-surface-variant'>
          {i18n.t('scenes.monitor.agentLogs.collect.description')}
        </p>
      </div>

      <Select
        aria-label={i18n.t(
          'scenes.monitor.agentLogs.collect.selectDatasetPlaceholder'
        )}
        placeholder={i18n.t(
          'scenes.monitor.agentLogs.collect.selectDatasetPlaceholder'
        )}
        selectedKeys={selectedDatasetId ? [selectedDatasetId] : []}
        onSelectionChange={keys => {
          const value = Array.from(keys)[0];
          onDatasetChange(value ? String(value) : null);
        }}
        isLoading={isLoading}
        isDisabled={isLoading}
        className='min-w-0 flex-1'
        classNames={{
          trigger: cn(
            'h-[42px] min-h-[42px] rounded-md border border-outline bg-surface',
            'data-[hover=true]:border-outline-focus'
          ),
          value: 'text-body-regular-md text-on-surface',
          popoverContent: 'rounded-md p-2 shadow-xl'
        }}
        listboxProps={{
          emptyContent: !hasDatasets ? (
            <NoDatasetsEmptyContent learnMoreHref={learnMoreHref} />
          ) : undefined
        }}
        data-testid='monitor-agent-logs-collect-dataset-select'
      >
        {datasets.map(dataset => (
          <SelectItem key={dataset.id} textValue={dataset.name}>
            {dataset.name}
          </SelectItem>
        ))}
      </Select>
    </div>
  );
}
